import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Modal, PasswordInput, Select, Switch, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useAxiosApi } from '@/hooks/useAxiosApi'

export default function ProfileCreate() {
    const [opened, handlers] = useDisclosure(false)
    const [withoutEmail, setWithoutEmail] = useState(false)
    const [saving, setSaving] = useState(false)
    const { requestPost } = useAxiosApi()
    const navigate = useNavigate()
    const form = useForm({
        initialValues: { name: '', email: '', role: 'MNP', password: '', pin: '' },
        validate: {
            name: (value) => value.trim() ? null : 'Le nom est obligatoire',
            email: (value) => withoutEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : 'Email invalide',
            password: (value) => withoutEmail || !value || value.length >= 10 ? null : '10 caractères minimum',
            pin: (value) => (!withoutEmail && !value) || /^\d{4}$/.test(value) ? null : 'Le PIN doit contenir 4 chiffres',
        },
    })

    const create = async (values) => {
        setSaving(true)
        try {
            const response = await requestPost('/profiles', {
                ...values,
                name: values.name.trim(),
                email: withoutEmail ? null : values.email.trim(),
                password: withoutEmail ? null : values.password || null,
                pin: values.pin || null,
            })
            notifications.show({ message: 'Utilisateur créé', color: 'green' })
            handlers.close()
            navigate(`/profiles/${response.data.profile.id}`)
        } catch {
            // API errors are displayed by useAxiosApi.
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <Button onClick={() => { form.reset(); setWithoutEmail(false); handlers.open() }}>
                Ajouter un utilisateur
            </Button>
            <Modal opened={opened} onClose={() => !saving && handlers.close()} title="Ajouter un utilisateur">
                <form onSubmit={form.onSubmit(create)} className="flex flex-col gap-4">
                    <TextInput label="Nom" required autoFocus {...form.getInputProps('name')} />
                    <Select label="Rôle" required allowDeselect={false}
                        data={[
                            { label: 'MNP', value: 'MNP' },
                            { label: 'Administrateur', value: 'ADMIN' },
                            ...(!withoutEmail ? [{ label: 'Revendeur', value: 'RETAILER' }] : []),
                        ]}
                        {...form.getInputProps('role')}
                    />
                    <Switch label="Sans email personnel" checked={withoutEmail} onChange={(event) => {
                        setWithoutEmail(event.currentTarget.checked)
                        form.clearErrors()
                        if (event.currentTarget.checked && form.values.role === 'RETAILER') form.setFieldValue('role', 'MNP')
                    }} />
                    {withoutEmail ? (
                        <Text size="sm" c="dimmed">
                            Une adresse technique sera créée automatiquement. La personne pourra utiliser son PIN sur un appareil autorisé.
                        </Text>
                    ) : (
                        <>
                            <TextInput label="Email" type="email" required {...form.getInputProps('email')} />
                            <PasswordInput label="Mot de passe initial (facultatif)" autoComplete="new-password"
                                description="10 caractères minimum. Sans mot de passe, la connexion par email reste disponible."
                                {...form.getInputProps('password')} />
                            <Text size="sm" c="dimmed">Aucun email d’invitation ne sera envoyé.</Text>
                        </>
                    )}
                    {form.values.role !== 'RETAILER' && (
                        <PasswordInput label="PIN de connexion rapide" required={withoutEmail}
                            description="4 chiffres — uniquement sur un appareil autorisé"
                            inputMode="numeric" maxLength={4} autoComplete="off" {...form.getInputProps('pin')} />
                    )}
                    <Button type="submit" loading={saving}>Créer l’utilisateur</Button>
                </form>
            </Modal>
        </>
    )
}
